import React from 'react'
import { useTheme } from '@/context/ThemeContext'
import type { ThisWeekPanel as ThisWeekPanelType, UnavailablePanel } from '../types'
import EmptyWorkspaceState from './EmptyWorkspaceState'

interface ThisWeekPanelProps {
  panel: ThisWeekPanelType | UnavailablePanel
}

// Four numbers, not a reporting surface — enough to feel the co-worker earning its
// keep. The scope label in the header is load-bearing: the same panel shows a
// member their own work and an admin the whole team's, and without saying which,
// both read the numbers the same way.
const ThisWeekPanel: React.FC<ThisWeekPanelProps> = ({ panel }) => {
  const { currentTheme: t } = useTheme()

  return (
    <section
      className="rounded-2xl border overflow-hidden min-w-0"
      style={{ backgroundColor: t.secondary, borderColor: t.border, boxShadow: t.cardShadow }}
      aria-label="This week"
    >
      <div className="flex items-center justify-between gap-3 px-4 pt-3.5 pb-3">
        <h2 className="text-[13px] font-semibold" style={{ color: t.text }}>
          This week
        </h2>
        {panel.status === 'ready' && (
          <span className="text-[10.5px] font-semibold uppercase tracking-wider truncate" style={{ color: t.mutedText }}>
            {panel.scopeLabel}
          </span>
        )}
      </div>

      {panel.status === 'unavailable' ? (
        <div className="px-4 pb-4">
          <EmptyWorkspaceState reason={panel.reason} />
        </div>
      ) : (
        <>
          {/* 1px grid lines drawn with the container background showing through, so
              the dividers follow the theme without four bordered boxes. */}
          <div className="grid grid-cols-2 gap-px border-t" style={{ backgroundColor: t.border, borderColor: t.border }}>
            {panel.metrics.map((metric) => (
              <div key={metric.key} className="px-4 py-3" style={{ backgroundColor: t.secondary }}>
                <b
                  className="block text-xl font-semibold tabular-nums leading-tight"
                  style={{ color: t.text, letterSpacing: '-0.02em' }}
                >
                  {metric.value}
                </b>
                <span className="text-[11px]" style={{ color: t.secondrytext }}>
                  {metric.label}
                </span>
              </div>
            ))}
          </div>

          {panel.credits && (
            <div className="px-4 py-3 border-t" style={{ borderColor: t.border }}>
              <b
                className="block text-xl font-semibold tabular-nums leading-tight"
                style={{ color: t.text, letterSpacing: '-0.02em' }}
              >
                {panel.credits.percentUsed}%
              </b>
              <span className="text-[11px]" style={{ color: t.secondrytext }}>
                of monthly credits used · {panel.credits.daysLeft}{' '}
                {panel.credits.daysLeft === 1 ? 'day' : 'days'} left
              </span>
            </div>
          )}
        </>
      )}
    </section>
  )
}

export default React.memo(ThisWeekPanel)
